import React, { useState } from "react";
import { motion } from "framer-motion";
import { InputField } from "../common/InputField";
import {
  formatScheduleDate,
  groupScheduleByDate,
  seatrc2026Schedule,
} from "../data/seatrc2026Schedule";

export const Schedule = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const scheduleData = groupScheduleByDate(seatrc2026Schedule);

  // Flatten for search but group for display
  const filteredSchedule = scheduleData.map(day => ({
    ...day,
    events: day.events.filter(event => 
      event.activity.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.time.toLowerCase().includes(searchQuery.toLowerCase()) ||
      formatScheduleDate(day.date).toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(day => day.events.length > 0);

  return (
    <section
      id="schedule"
      className="w-full min-h-screen py-20 text-center text-red-900 flex-col justify-center relative bg-white"
    >
      <div className="relative z-0 container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-6xl mx-auto space-y-4">
            
            {/* Additional Note Banner */}
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-left"
            >
              <p className="text-red-800 font-medium text-sm">
                <span className="font-bold">Important:</span> Latest schedule from the event organizer. Subject to change.
              </p>
            </motion.div>

            <motion.div className="bg-white rounded-2xl border border-gray-200 text-left relative overflow-hidden max-w-6xl mx-auto"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }} viewport={{ once: true }}>
              
              <div className="flex justify-between items-center mb-4 px-4 pt-6">
                <h2 className="text-lg uppercase font-bold text-red-900">Event Schedule (July 24 - 26, 2026)</h2>
                <div className="w-full max-w-xs">
                  <InputField
                    name="search"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search schedule..."
                    labelColor="text-red-800"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full table-auto divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-1/4">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-1/6">Time</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-1/3">Activity</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-1/4">Location</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredSchedule.length > 0 ? (
                      filteredSchedule.map((day, dayIndex) => (
                        <React.Fragment key={dayIndex}>
                          {day.events.map((event, eventIndex) => (
                            <tr key={`${dayIndex}-${eventIndex}`} className={eventIndex % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                              {eventIndex === 0 && (
                                <td 
                                  rowSpan={day.events.length} 
                                  className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 align-top bg-white border-r border-gray-100"
                                >
                                  {formatScheduleDate(day.date)}
                                </td>
                              )}
                              <td className="px-6 py-4 whitespace-normal text-sm text-gray-600 align-top">
                                {event.time}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-800 align-top">
                                <div className="font-medium">{event.activity}</div>
                                {event.note && (
                                  <p className="mt-1 text-xs text-gray-500 italic">
                                    {event.note}
                                  </p>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-normal text-sm text-gray-600 align-top">
                                {event.location}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                          No schedule found matching your search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </section>
  );
};
